import { mat4, vec4 } from 'gl-matrix';

/**
 * How the entire model is represented in a scenegraph.
 */
export type Model = {
  name: string;
  scene: GroupNode | LeafNode;
  sceneNodeType: NodeType;
}

export type CameraAnimationState = {
  orbit: number,
  orbit_v: number,
  orbit_a: number,
  time: number
}

/**
 * Represents the connection between a group node and
 * either another group node or a leaf node. This
 * connection can involve a transformation or it could
 * not.
 */
export type Edge = {
  to: GroupNode | LeafNode;
  toType: NodeType;
  transformation?: mat4;
}

export enum Camera {
  ORBIT,
  SURVEY
}

export type CameraMap = Map<Camera, CameraProperties>


export type CameraProperties = {
  mat: mat4,
  animFunc: (camera: mat4) => mat4
}


/**
 * Enumeration to aid in the identification of
 * a node being of a particular quality; that 
 * quality being whether it is of the leaf family
 * or of the group family respectively.
 */
export enum NodeType {
  GROUP_NODE,
  LEAF_NODE
}

/**
 * The graph nodes of a scene graph, which a larger group
 * of nodes being rendered together onto the scene.
 */
export type GroupNode = {
  name: string;
  edges: Edge[];
}

/**
 * The leaf nodes of a scene graph, which represent
 * an actual base instance being rendered onto the scene.
 */
export type LeafNode = {
  name: string;
  instance: InstanceType; // vbo id
  color: vec4;
}

/**
 * Enumeration to describe which base instance the
 * leaf node is rendering in a SceneGraph renderer.
 */
export enum InstanceType {
  CUBE = 0,
  CYLINDER = 1,
  SPHERE = 2,
  SEMI_SPHERE = 3,
  FAN = 4,
}

/**
 * How to find the components for the model after they
 * are loaded into the GPU.
 */
export type BaseInstance = {
  id: InstanceType;
  vbo: WebGLBuffer;
  ibo: WebGLBuffer;
  numVertices: number;
  numIndices: number;
}
